import Cart from "cart-store";

const SOURCE = "ski_socks";
const UNISEX = "unisex";
const AUDIENCE_PRIORITY = ["kids", "women", "men"];
const DEFAULT_MAX_CARDS = 2;
const MAX_TAG_LOOKUPS = 3;

/**
 * Short keywords ("men", "man") are only ever matched as whole tokens, because
 * they are substrings of the opposite audience ("women", "woman"). Longer ones
 * may also match inside a word so that handles without separators
 * ("herenjas") still resolve.
 */
const MIN_SUBSTRING_KEYWORD_LENGTH = 5;

const productCache = new Map();

const SOCKS_PRODUCTS = (() => {
  const el = document.getElementById("cart-socks-data");
  if (!el) return [];
  try {
    const parsed = JSON.parse(el.textContent || "{}");
    return Array.isArray(parsed?.products) ? parsed.products : [];
  } catch {
    return [];
  }
})();

const SOCK_HANDLES = new Set(SOCKS_PRODUCTS.map((p) => p.handle));

function parseKeywords(value) {
  return String(value || "")
    .toLowerCase()
    .split(",")
    .map((keyword) => keyword.trim())
    .filter(Boolean);
}

function tokenize(haystack) {
  return haystack.split(/[^a-z0-9]+/).filter(Boolean);
}

function itemHaystack(item) {
  return `${item?.handle || ""} ${item?.product_title || item?.title || ""} ${item?.product_type || ""}`.toLowerCase();
}

function escapeHtml(value) {
  return String(value).replace(
    /[&<>"']/g,
    (char) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[char],
  );
}

/**
 * Splits a product into its colour and size axes. Sizes are reported per colour
 * because availability differs between colours: a sock can be in stock in 39-42
 * in one colourway and sold out in the same size in another.
 */
function buildVariantModel(product) {
  const colorPosition = product.optionRoles?.color ?? null;
  const sizePosition = product.optionRoles?.size ?? null;
  const allVariants = product.variants || [];
  const variants = allVariants.filter((v) => v.available);

  const valueAt = (variant, position) =>
    position ? (variant.options?.[position - 1] ?? null) : null;
  const distinct = (values) => [...new Set(values.filter(Boolean))];

  /* Canonical value order is taken from every variant, not just the in-stock
     ones, so a sold-out size cannot reorder the list a shopper sees. */
  const allColors = colorPosition ? distinct(allVariants.map((v) => valueAt(v, colorPosition))) : [];
  const allSizes = sizePosition ? distinct(allVariants.map((v) => valueAt(v, sizePosition))) : [];

  const hasStock = (color, size) =>
    variants.some(
      (v) =>
        (!colorPosition || !color || valueAt(v, colorPosition) === color) &&
        (!sizePosition || !size || valueAt(v, sizePosition) === size),
    );

  const colors = allColors.filter((color) => hasStock(color, null));
  const sizesFor = (color) => allSizes.filter((size) => hasStock(color, size));

  const resolve = ({ color, size }) =>
    variants.find(
      (v) =>
        (!colorPosition || !color || valueAt(v, colorPosition) === color) &&
        (!sizePosition || !size || valueAt(v, sizePosition) === size),
    ) ?? variants[0];

  return { variants, colors, sizesFor, resolve };
}

/**
 * Socks sell best in the middle size, so that is the default rather than the
 * first in-stock one. An even number of sizes rounds down to the lower middle.
 */
function middleValue(values) {
  if (!values.length) return null;
  return values[Math.floor((values.length - 1) / 2)];
}

function optionsHtml(values, selected) {
  return values
    .map(
      (value) =>
        `<option value="${escapeHtml(value)}"${value === selected ? " selected" : ""}>${escapeHtml(value)}</option>`,
    )
    .join("");
}

class CartSocksUpsell extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute("data-mounted")) return;
    this.setAttribute("data-mounted", "");
    if (!SOCKS_PRODUCTS.length) return;

    this.list = this.querySelector("[data-list]");
    if (!this.list) return;

    this.maxCards = Math.max(1, parseInt(this.dataset.maxCards, 10) || DEFAULT_MAX_CARDS);
    this.keywords = {
      kids: parseKeywords(this.dataset.keywordsKids),
      women: parseKeywords(this.dataset.keywordsWomen),
      men: parseKeywords(this.dataset.keywordsMen),
    };
    this.sockKeywords = parseKeywords(this.dataset.sockKeywords);
    this.i18n = {
      add: this.dataset.i18nAdd || "Add",
      size: this.dataset.i18nSize || "Size",
      color: this.dataset.i18nColor || "Color",
    };

    this.isVisible = false;
    this.shownEmitted = false;
    this.renderKey = null;
    this.selection = new Map();
    this.evaluateToken = 0;

    this.#evaluate(Cart.getState().cart);
    this.unsubscribe = Cart.subscribe((state) => this.#evaluate(state.cart));
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  async #evaluate(cart) {
    const items = cart?.items || [];
    const token = ++this.evaluateToken;

    if (!items.length || this.#cartHasSocks(items)) {
      this.#reset();
      return;
    }

    let audience = this.#audienceFromLineItems(items);
    if (!audience) {
      audience = await this.#audienceFromProductTags(items);
      if (token !== this.evaluateToken) return;
    }

    const products = this.#pickProducts(audience);
    if (!products.length) {
      this.#reset();
      return;
    }

    const renderKey = `${audience || UNISEX}|${products.map((p) => p.handle).join(",")}`;
    if (renderKey !== this.renderKey) {
      this.renderKey = renderKey;
      this.#renderCards(products);
    }
    this.#setVisible(true);

    if (!this.shownEmitted) {
      this.shownEmitted = true;
      this.#emit("socks_shown", { audience, variant_id: null });
    }
  }

  #reset() {
    this.#setVisible(false);
    this.renderKey = null;
    this.shownEmitted = false;
  }

  /**
   * Deliberately a plain substring test: any sock-shaped line item should
   * suppress the card, including socks that are not in the configured
   * collection.
   */
  #cartHasSocks(items) {
    return items.some((item) => {
      if (item?.handle && SOCK_HANDLES.has(item.handle)) return true;
      const haystack = itemHaystack(item);
      return this.sockKeywords.some((keyword) => haystack.includes(keyword));
    });
  }

  #audienceFromLineItems(items) {
    for (let i = items.length - 1; i >= 0; i -= 1) {
      const audience = this.#detectAudience(itemHaystack(items[i]));
      if (audience) return audience;
    }
    return null;
  }

  async #audienceFromProductTags(items) {
    const candidates = items.slice(-MAX_TAG_LOOKUPS).reverse();
    for (const item of candidates) {
      if (!item?.handle) continue;
      let product;
      try {
        product = await this.#fetchProduct(item.handle);
      } catch {
        continue;
      }
      const tags = Array.isArray(product?.tags) ? product.tags.join(" ") : "";
      const audience = this.#detectAudience(`${tags} ${product?.type || ""}`.toLowerCase());
      if (audience) return audience;
    }
    return null;
  }

  #detectAudience(haystack) {
    const tokens = new Set(tokenize(haystack));
    for (const audience of AUDIENCE_PRIORITY) {
      const keywords = this.keywords[audience];
      const hit = keywords.some(
        (keyword) =>
          tokens.has(keyword) ||
          (keyword.length >= MIN_SUBSTRING_KEYWORD_LENGTH && haystack.includes(keyword)),
      );
      if (hit) return audience;
    }
    return null;
  }

  #pickProducts(audience) {
    const sellable = SOCKS_PRODUCTS.filter((p) => p.variants?.some((v) => v.available));
    const matching = audience ? sellable.filter((p) => p.audience === audience) : [];
    const unisex = sellable.filter((p) => p.audience === UNISEX);
    return [...matching, ...unisex].slice(0, this.maxCards);
  }

  async #fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    const res = await fetch(`${window.Shopify?.routes?.root ?? "/"}products/${handle}.js`);
    if (!res.ok) throw new Error(`Product fetch failed: ${res.status}`);
    const data = await res.json();
    productCache.set(handle, data);
    return data;
  }

  #renderCards(products) {
    this.selection.clear();
    const frag = document.createDocumentFragment();
    products.forEach((product, index) => {
      const card = this.#buildCard(product, index);
      if (card) frag.appendChild(card);
    });
    this.list.replaceChildren(frag);
  }

  #buildCard(product, index) {
    const model = buildVariantModel(product);
    if (!model.variants.length) return null;

    const state = { color: model.colors[0] ?? null, size: null };
    state.size = middleValue(model.sizesFor(state.color));

    const showColorSelect = model.colors.length > 1;
    const showSizeSelect = model.sizesFor(null).length > 1;
    const meta = [showColorSelect ? null : state.color, showSizeSelect ? null : state.size]
      .filter(Boolean)
      .join(" · ");

    const li = document.createElement("li");
    li.className = "cart-socks-upsell__item";
    li.innerHTML = `
      <a class="cart-socks-upsell__media" href="${escapeHtml(product.url || "#")}" tabindex="-1" aria-hidden="true">
        ${
          product.image
            ? `<img class="cart-socks-upsell__img" data-image src="${escapeHtml(product.image)}" alt="" loading="lazy" width="60" height="60">`
            : `<span class="cart-socks-upsell__img cart-socks-upsell__img--empty"></span>`
        }
      </a>
      <div class="cart-socks-upsell__info">
        <a class="cart-socks-upsell__name" href="${escapeHtml(product.url || "#")}">${escapeHtml(product.title)}</a>
        <p class="cart-socks-upsell__price" data-price>
          <span data-current-price></span>
          <s data-compare-price hidden></s>
          ${meta ? `<span class="cart-socks-upsell__meta">${escapeHtml(meta)}</span>` : ""}
        </p>
        <div class="cart-socks-upsell__actions">
          ${
            showColorSelect
              ? `<select class="cart-socks-upsell__select" data-color-select aria-label="${escapeHtml(this.i18n.color)}">${optionsHtml(model.colors, state.color)}</select>`
              : ""
          }
          ${
            showSizeSelect
              ? `<select class="cart-socks-upsell__select" data-size-select aria-label="${escapeHtml(this.i18n.size)}">${optionsHtml(model.sizesFor(state.color), state.size)}</select>`
              : ""
          }
          <button type="button" class="cart-socks-upsell__add" data-add>${escapeHtml(this.i18n.add)}</button>
        </div>
      </div>
    `;

    const colorSelect = li.querySelector("[data-color-select]");
    const sizeSelect = li.querySelector("[data-size-select]");

    colorSelect?.addEventListener("change", () => {
      state.color = colorSelect.value;
      const sizes = model.sizesFor(state.color);
      state.size = middleValue(sizes);
      if (sizeSelect) sizeSelect.innerHTML = optionsHtml(sizes, state.size);
      this.#applyVariant(li, index, model.resolve(state));
    });

    sizeSelect?.addEventListener("change", () => {
      state.size = sizeSelect.value;
      this.#applyVariant(li, index, model.resolve(state));
    });

    li.querySelector("[data-add]").addEventListener("click", (event) =>
      this.#addSelected(index, event.currentTarget),
    );

    this.#applyVariant(li, index, model.resolve(state));
    return li;
  }

  #applyVariant(li, index, variant) {
    if (!variant) return;
    this.selection.set(index, variant);

    const current = li.querySelector("[data-current-price]");
    const compare = li.querySelector("[data-compare-price]");
    const image = li.querySelector("[data-image]");
    if (current) current.textContent = variant.price || "";
    if (compare) {
      compare.textContent = variant.compareAtPrice || "";
      compare.hidden = !variant.compareAtPrice;
    }
    if (image && variant.image) image.src = variant.image;
  }

  async #addSelected(index, button) {
    const variant = this.selection.get(index);
    if (!variant || button.disabled) return;
    button.disabled = true;
    button.classList.add("is-loading");
    this.#emit("socks_click", { variant_id: variant.id });
    try {
      await Cart.add({ id: variant.id, quantity: 1 });
    } catch {
      // cart store dispatches its own on:cart:failed
    } finally {
      button.disabled = false;
      button.classList.remove("is-loading");
    }
  }

  #setVisible(show) {
    if (this.isVisible === show) return;
    this.isVisible = show;
    if (show) {
      this.removeAttribute("hidden");
      this.style.display = "";
    } else {
      this.setAttribute("hidden", "");
      this.style.display = "none";
    }
  }

  #emit(name, extra) {
    const detail = {
      audience: extra?.audience ?? null,
      variant_id: extra?.variant_id ?? null,
      source: SOURCE,
    };
    document.dispatchEvent(new CustomEvent(`upsell:${name}`, { detail }));
    if (window.dataLayer && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: name, ...detail });
    }
  }
}

if (!customElements.get("cart-socks-upsell")) {
  customElements.define("cart-socks-upsell", CartSocksUpsell);
}
